/**
 * Reviewed data for the Ions & Bonding Lab (study-chemistry-tools-prd.md §8.2 `ions-bonding`).
 * Answer truth lives here and in `ionsBonding.ts`, never in JSX.
 */

export type IonsBondingMode = 'ions' | 'bonding' | 'senior';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const IONS_BONDING_TOOL_IDENTITY = {
  id: 'ions-bonding',
  toolKey: 'chemistry-ions-bonding',
  toolVersion: '1.0.0',
  lifecycle: 'building',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const IONS_BONDING_START_YEAR = 10;

/** All modes in ascending start Year. `bonding` is learner-selectable from Year 10. */
export const IONS_BONDING_MODES: readonly { mode: IonsBondingMode; fromYear: number }[] = [
  { mode: 'ions', fromYear: 10 },
  { mode: 'bonding', fromYear: 10 },
  { mode: 'senior', fromYear: 11 },
];

/**
 * Modes a `?year` value may open by default. Year 10 opens `ions` (week 3) and the learner moves
 * to `bonding` (week 4) by hand, so `bonding` is not a default target.
 */
export const IONS_BONDING_DEFAULT_MODES: readonly { mode: IonsBondingMode; fromYear: number }[] =
  IONS_BONDING_MODES.filter((entry) => entry.mode !== 'bonding');

export interface IonsBondingProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const IONS_BONDING_PROVENANCE: IonsBondingProvenance = {
  sources: [
    'IUPAC Nomenclature of Inorganic Chemistry (Red Book, 2005) — ion names and formula conventions',
    'CRC Handbook of Chemistry and Physics, 97th edition (2016) — melting points and Pauling electronegativities',
    'NIST Computational Chemistry Comparison and Benchmark Database (CCCBDB) — experimental gas-phase bond angles',
    'Standard senior secondary chemistry textbook treatment of the shell model, dot-and-cross diagrams and VSEPR',
  ],
  conditions:
    'Melting points at 100 kPa, rounded to the nearest whole °C. Conductivity and solubility describe pure substances near 25 °C. Bond angles are gas-phase values for isolated molecules.',
  reviewStatus: 'pending-teaching-review',
};

export type ElementSymbol =
  | 'H'
  | 'Li'
  | 'B'
  | 'C'
  | 'N'
  | 'O'
  | 'F'
  | 'Ne'
  | 'Na'
  | 'Mg'
  | 'Al'
  | 'Si'
  | 'P'
  | 'S'
  | 'Cl'
  | 'Ar'
  | 'K'
  | 'Ca'
  | 'Fe';

export interface ElementFact {
  symbol: ElementSymbol;
  name: string;
  atomicNumber: number;
  /** Outer-shell (valence) electrons for main-group elements. */
  valenceElectrons: number;
  /** Pauling electronegativity (CRC). Null when not used by this tool. */
  electronegativity: number | null;
}

export const ELEMENT_FACTS: Record<ElementSymbol, ElementFact> = {
  H: { symbol: 'H', name: 'Hydrogen', atomicNumber: 1, valenceElectrons: 1, electronegativity: 2.2 },
  Li: { symbol: 'Li', name: 'Lithium', atomicNumber: 3, valenceElectrons: 1, electronegativity: 0.98 },
  B: { symbol: 'B', name: 'Boron', atomicNumber: 5, valenceElectrons: 3, electronegativity: 2.04 },
  C: { symbol: 'C', name: 'Carbon', atomicNumber: 6, valenceElectrons: 4, electronegativity: 2.55 },
  N: { symbol: 'N', name: 'Nitrogen', atomicNumber: 7, valenceElectrons: 5, electronegativity: 3.04 },
  O: { symbol: 'O', name: 'Oxygen', atomicNumber: 8, valenceElectrons: 6, electronegativity: 3.44 },
  F: { symbol: 'F', name: 'Fluorine', atomicNumber: 9, valenceElectrons: 7, electronegativity: 3.98 },
  Ne: { symbol: 'Ne', name: 'Neon', atomicNumber: 10, valenceElectrons: 8, electronegativity: null },
  Na: { symbol: 'Na', name: 'Sodium', atomicNumber: 11, valenceElectrons: 1, electronegativity: 0.93 },
  Mg: { symbol: 'Mg', name: 'Magnesium', atomicNumber: 12, valenceElectrons: 2, electronegativity: 1.31 },
  Al: { symbol: 'Al', name: 'Aluminium', atomicNumber: 13, valenceElectrons: 3, electronegativity: 1.61 },
  Si: { symbol: 'Si', name: 'Silicon', atomicNumber: 14, valenceElectrons: 4, electronegativity: 1.9 },
  P: { symbol: 'P', name: 'Phosphorus', atomicNumber: 15, valenceElectrons: 5, electronegativity: 2.19 },
  S: { symbol: 'S', name: 'Sulfur', atomicNumber: 16, valenceElectrons: 6, electronegativity: 2.58 },
  Cl: { symbol: 'Cl', name: 'Chlorine', atomicNumber: 17, valenceElectrons: 7, electronegativity: 3.16 },
  Ar: { symbol: 'Ar', name: 'Argon', atomicNumber: 18, valenceElectrons: 8, electronegativity: null },
  K: { symbol: 'K', name: 'Potassium', atomicNumber: 19, valenceElectrons: 1, electronegativity: 0.82 },
  Ca: { symbol: 'Ca', name: 'Calcium', atomicNumber: 20, valenceElectrons: 2, electronegativity: 1.0 },
  Fe: { symbol: 'Fe', name: 'Iron', atomicNumber: 26, valenceElectrons: 2, electronegativity: 1.83 },
};

/* ------------------------------------------------------------------ ions */

/** What a reviewed element does under the Year 10 shell model. */
export type IonOutcome =
  | { kind: 'ion'; charge: number }
  | { kind: 'no-simple-ion'; reason: string };

export interface IonElementCase {
  id: string;
  symbol: ElementSymbol;
  /** Reviewed expected outcome. The shell rule in `ionsBonding.ts` must reproduce it. */
  expected: IonOutcome;
  ionName?: string;
}

/** Z ≤ 20 elements whose simple ion (or lack of one) the shell rule predicts. */
export const ION_ELEMENT_CASES: IonElementCase[] = [
  { id: 'lithium', symbol: 'Li', expected: { kind: 'ion', charge: 1 }, ionName: 'lithium ion' },
  { id: 'sodium', symbol: 'Na', expected: { kind: 'ion', charge: 1 }, ionName: 'sodium ion' },
  { id: 'potassium', symbol: 'K', expected: { kind: 'ion', charge: 1 }, ionName: 'potassium ion' },
  { id: 'magnesium', symbol: 'Mg', expected: { kind: 'ion', charge: 2 }, ionName: 'magnesium ion' },
  { id: 'calcium', symbol: 'Ca', expected: { kind: 'ion', charge: 2 }, ionName: 'calcium ion' },
  { id: 'aluminium', symbol: 'Al', expected: { kind: 'ion', charge: 3 }, ionName: 'aluminium ion' },
  { id: 'nitrogen', symbol: 'N', expected: { kind: 'ion', charge: -3 }, ionName: 'nitride ion' },
  { id: 'phosphorus', symbol: 'P', expected: { kind: 'ion', charge: -3 }, ionName: 'phosphide ion' },
  { id: 'oxygen', symbol: 'O', expected: { kind: 'ion', charge: -2 }, ionName: 'oxide ion' },
  { id: 'sulfur', symbol: 'S', expected: { kind: 'ion', charge: -2 }, ionName: 'sulfide ion' },
  { id: 'fluorine', symbol: 'F', expected: { kind: 'ion', charge: -1 }, ionName: 'fluoride ion' },
  { id: 'chlorine', symbol: 'Cl', expected: { kind: 'ion', charge: -1 }, ionName: 'chloride ion' },
  {
    id: 'carbon',
    symbol: 'C',
    expected: {
      kind: 'no-simple-ion',
      reason:
        'Carbon has 4 outer electrons. Losing or gaining 4 electrons needs too much energy, so carbon shares electrons in covalent bonds instead.',
    },
  },
  {
    id: 'neon',
    symbol: 'Ne',
    expected: {
      kind: 'no-simple-ion',
      reason: 'Neon already has a full outer shell (2,8). It has no need to lose or gain electrons.',
    },
  },
  {
    id: 'argon',
    symbol: 'Ar',
    expected: {
      kind: 'no-simple-ion',
      reason: 'Argon already has a full outer shell (2,8,8). It has no need to lose or gain electrons.',
    },
  },
];

/**
 * Elements the shell rule must refuse. The page shows them to model fail-closed behaviour: the
 * learner is told why the simple rule stops, not a guessed charge.
 */
export const ION_UNSUPPORTED_CASES: { id: string; symbol: ElementSymbol; reason: string }[] = [
  {
    id: 'iron',
    symbol: 'Fe',
    reason:
      'Iron (Z = 26) is a transition metal. Its electrons do not fill the simple 2,8,8 shells, and it forms both Fe²⁺ and Fe³⁺. Read the charge from the name: iron(II) or iron(III).',
  },
  {
    id: 'hydrogen',
    symbol: 'H',
    reason:
      'Hydrogen can lose its electron (H⁺, in acids) or gain one (H⁻, in metal hydrides). The shell rule alone cannot choose, so this tool does not predict it.',
  },
  {
    id: 'boron',
    symbol: 'B',
    reason:
      'Boron has 3 outer electrons, but it bonds covalently in almost all its compounds. The simple “lose 3” rule would wrongly predict B³⁺.',
  },
];

export interface IonSpecies {
  id: string;
  name: string;
  /** Formula without charge, using Unicode subscripts, e.g. 'SO₄'. */
  core: string;
  charge: number;
  polyatomic: boolean;
  /** Where the charge comes from: the shell rule or reviewed reference data. */
  basis: 'shell-rule' | 'reviewed-data';
}

export const ION_SPECIES: IonSpecies[] = [
  { id: 'Li+', name: 'lithium', core: 'Li', charge: 1, polyatomic: false, basis: 'shell-rule' },
  { id: 'Na+', name: 'sodium', core: 'Na', charge: 1, polyatomic: false, basis: 'shell-rule' },
  { id: 'K+', name: 'potassium', core: 'K', charge: 1, polyatomic: false, basis: 'shell-rule' },
  { id: 'Ag+', name: 'silver', core: 'Ag', charge: 1, polyatomic: false, basis: 'reviewed-data' },
  { id: 'NH4+', name: 'ammonium', core: 'NH₄', charge: 1, polyatomic: true, basis: 'reviewed-data' },
  { id: 'Mg2+', name: 'magnesium', core: 'Mg', charge: 2, polyatomic: false, basis: 'shell-rule' },
  { id: 'Ca2+', name: 'calcium', core: 'Ca', charge: 2, polyatomic: false, basis: 'shell-rule' },
  { id: 'Cu2+', name: 'copper(II)', core: 'Cu', charge: 2, polyatomic: false, basis: 'reviewed-data' },
  { id: 'Zn2+', name: 'zinc', core: 'Zn', charge: 2, polyatomic: false, basis: 'reviewed-data' },
  { id: 'Fe2+', name: 'iron(II)', core: 'Fe', charge: 2, polyatomic: false, basis: 'reviewed-data' },
  { id: 'Fe3+', name: 'iron(III)', core: 'Fe', charge: 3, polyatomic: false, basis: 'reviewed-data' },
  { id: 'Al3+', name: 'aluminium', core: 'Al', charge: 3, polyatomic: false, basis: 'shell-rule' },
  { id: 'F-', name: 'fluoride', core: 'F', charge: -1, polyatomic: false, basis: 'shell-rule' },
  { id: 'Cl-', name: 'chloride', core: 'Cl', charge: -1, polyatomic: false, basis: 'shell-rule' },
  { id: 'OH-', name: 'hydroxide', core: 'OH', charge: -1, polyatomic: true, basis: 'reviewed-data' },
  { id: 'NO3-', name: 'nitrate', core: 'NO₃', charge: -1, polyatomic: true, basis: 'reviewed-data' },
  { id: 'HCO3-', name: 'hydrogencarbonate', core: 'HCO₃', charge: -1, polyatomic: true, basis: 'reviewed-data' },
  { id: 'O2-', name: 'oxide', core: 'O', charge: -2, polyatomic: false, basis: 'shell-rule' },
  { id: 'S2-', name: 'sulfide', core: 'S', charge: -2, polyatomic: false, basis: 'shell-rule' },
  { id: 'SO42-', name: 'sulfate', core: 'SO₄', charge: -2, polyatomic: true, basis: 'reviewed-data' },
  { id: 'CO32-', name: 'carbonate', core: 'CO₃', charge: -2, polyatomic: true, basis: 'reviewed-data' },
  { id: 'N3-', name: 'nitride', core: 'N', charge: -3, polyatomic: false, basis: 'shell-rule' },
  { id: 'PO43-', name: 'phosphate', core: 'PO₄', charge: -3, polyatomic: true, basis: 'reviewed-data' },
];

export interface IonicCompoundCase {
  id: string;
  name: string;
  cationId: string;
  anionId: string;
  /** Reviewed formula. The criss-cross model must reproduce it exactly. */
  formula: string;
}

export const IONIC_COMPOUND_CASES: IonicCompoundCase[] = [
  { id: 'sodium-chloride', name: 'Sodium chloride', cationId: 'Na+', anionId: 'Cl-', formula: 'NaCl' },
  { id: 'magnesium-oxide', name: 'Magnesium oxide', cationId: 'Mg2+', anionId: 'O2-', formula: 'MgO' },
  { id: 'calcium-chloride', name: 'Calcium chloride', cationId: 'Ca2+', anionId: 'Cl-', formula: 'CaCl₂' },
  { id: 'potassium-sulfide', name: 'Potassium sulfide', cationId: 'K+', anionId: 'S2-', formula: 'K₂S' },
  { id: 'aluminium-oxide', name: 'Aluminium oxide', cationId: 'Al3+', anionId: 'O2-', formula: 'Al₂O₃' },
  { id: 'magnesium-nitride', name: 'Magnesium nitride', cationId: 'Mg2+', anionId: 'N3-', formula: 'Mg₃N₂' },
  { id: 'aluminium-fluoride', name: 'Aluminium fluoride', cationId: 'Al3+', anionId: 'F-', formula: 'AlF₃' },
  { id: 'calcium-hydroxide', name: 'Calcium hydroxide', cationId: 'Ca2+', anionId: 'OH-', formula: 'Ca(OH)₂' },
  { id: 'ammonium-sulfate', name: 'Ammonium sulfate', cationId: 'NH4+', anionId: 'SO42-', formula: '(NH₄)₂SO₄' },
  { id: 'aluminium-sulfate', name: 'Aluminium sulfate', cationId: 'Al3+', anionId: 'SO42-', formula: 'Al₂(SO₄)₃' },
  { id: 'magnesium-phosphate', name: 'Magnesium phosphate', cationId: 'Mg2+', anionId: 'PO43-', formula: 'Mg₃(PO₄)₂' },
  { id: 'sodium-hydrogencarbonate', name: 'Sodium hydrogencarbonate', cationId: 'Na+', anionId: 'HCO3-', formula: 'NaHCO₃' },
  { id: 'iron-iii-oxide', name: 'Iron(III) oxide', cationId: 'Fe3+', anionId: 'O2-', formula: 'Fe₂O₃' },
  { id: 'iron-ii-chloride', name: 'Iron(II) chloride', cationId: 'Fe2+', anionId: 'Cl-', formula: 'FeCl₂' },
  { id: 'copper-ii-sulfate', name: 'Copper(II) sulfate', cationId: 'Cu2+', anionId: 'SO42-', formula: 'CuSO₄' },
  { id: 'zinc-chloride', name: 'Zinc chloride', cationId: 'Zn2+', anionId: 'Cl-', formula: 'ZnCl₂' },
  { id: 'silver-nitrate', name: 'Silver nitrate', cationId: 'Ag+', anionId: 'NO3-', formula: 'AgNO₃' },
  { id: 'sodium-carbonate', name: 'Sodium carbonate', cationId: 'Na+', anionId: 'CO32-', formula: 'Na₂CO₃' },
  { id: 'iron-iii-hydroxide', name: 'Iron(III) hydroxide', cationId: 'Fe3+', anionId: 'OH-', formula: 'Fe(OH)₃' },
  { id: 'ammonium-phosphate', name: 'Ammonium phosphate', cationId: 'NH4+', anionId: 'PO43-', formula: '(NH₄)₃PO₄' },
];

/* --------------------------------------------------------- molecule shapes */

export interface DiagramAtom {
  element: ElementSymbol;
  /** Position in bond-length units; the renderer scales it. */
  x: number;
  y: number;
  lonePairs: number;
  /** Dot-and-cross symbol for this atom's own electrons. */
  marker: 'dot' | 'cross';
}

export interface DiagramBond {
  from: number;
  to: number;
  order: 1 | 2 | 3;
}

export interface MolecularStructure {
  atoms: DiagramAtom[];
  bonds: DiagramBond[];
  /** Overall charge of the species (0 for a molecule). */
  charge: number;
}

const DEG = Math.PI / 180;
const BOND = 1;

/** Point `BOND` units from (x, y) in direction `angle` (degrees, screen axes: y grows downwards). */
function from(x: number, y: number, angle: number): { x: number; y: number } {
  return {
    x: Math.round((x + BOND * Math.cos(angle * DEG)) * 1000) / 1000,
    y: Math.round((y + BOND * Math.sin(angle * DEG)) * 1000) / 1000,
  };
}

function atom(
  element: ElementSymbol,
  point: { x: number; y: number },
  lonePairs: number,
  marker: 'dot' | 'cross',
): DiagramAtom {
  return { element, x: point.x, y: point.y, lonePairs, marker };
}

const ORIGIN = { x: 0, y: 0 };

/** Bonds from atom 0 to atoms 1…count, all of the same order. */
function starBonds(count: number, order: 1 | 2 | 3 = 1): DiagramBond[] {
  return Array.from({ length: count }, (_, index) => ({ from: 0, to: index + 1, order }));
}

export interface CovalentMoleculeCase {
  id: string;
  name: string;
  formula: string;
  structure: MolecularStructure;
  note: string;
}

export const COVALENT_MOLECULE_CASES: CovalentMoleculeCase[] = [
  {
    id: 'hydrogen',
    name: 'Hydrogen',
    formula: 'H₂',
    structure: {
      charge: 0,
      atoms: [atom('H', ORIGIN, 0, 'dot'), atom('H', from(0, 0, 0), 0, 'cross')],
      bonds: [{ from: 0, to: 1, order: 1 }],
    },
    note: 'Each hydrogen needs only 2 electrons to fill its first shell, so one shared pair is enough.',
  },
  {
    id: 'chlorine',
    name: 'Chlorine',
    formula: 'Cl₂',
    structure: {
      charge: 0,
      atoms: [atom('Cl', ORIGIN, 3, 'dot'), atom('Cl', from(0, 0, 0), 3, 'cross')],
      bonds: [{ from: 0, to: 1, order: 1 }],
    },
    note: 'Each chlorine has 7 outer electrons and needs 1 more, so the atoms share one pair.',
  },
  {
    id: 'hydrogen-chloride',
    name: 'Hydrogen chloride',
    formula: 'HCl',
    structure: {
      charge: 0,
      atoms: [atom('H', ORIGIN, 0, 'dot'), atom('Cl', from(0, 0, 0), 3, 'cross')],
      bonds: [{ from: 0, to: 1, order: 1 }],
    },
    note: 'Hydrogen and chlorine each need one more electron, so they share one pair.',
  },
  {
    id: 'water',
    name: 'Water',
    formula: 'H₂O',
    structure: {
      charge: 0,
      atoms: [atom('O', ORIGIN, 2, 'cross'), atom('H', from(0, 0, 180), 0, 'dot'), atom('H', from(0, 0, 90), 0, 'dot')],
      bonds: starBonds(2),
    },
    note: 'Oxygen needs 2 more electrons, so it shares one pair with each of two hydrogen atoms.',
  },
  {
    id: 'ammonia',
    name: 'Ammonia',
    formula: 'NH₃',
    structure: {
      charge: 0,
      atoms: [
        atom('N', ORIGIN, 1, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
      ],
      bonds: starBonds(3),
    },
    note: 'Nitrogen needs 3 more electrons, so it shares one pair with each of three hydrogen atoms.',
  },
  {
    id: 'methane',
    name: 'Methane',
    formula: 'CH₄',
    structure: {
      charge: 0,
      atoms: [
        atom('C', ORIGIN, 0, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
        atom('H', from(0, 0, 270), 0, 'dot'),
      ],
      bonds: starBonds(4),
    },
    note: 'Carbon needs 4 more electrons, so it shares one pair with each of four hydrogen atoms.',
  },
  {
    id: 'oxygen',
    name: 'Oxygen',
    formula: 'O₂',
    structure: {
      charge: 0,
      atoms: [atom('O', ORIGIN, 2, 'dot'), atom('O', from(0, 0, 0), 2, 'cross')],
      bonds: [{ from: 0, to: 1, order: 2 }],
    },
    note: 'Each oxygen needs 2 more electrons, so the two atoms share two pairs: a double bond.',
  },
  {
    id: 'nitrogen',
    name: 'Nitrogen',
    formula: 'N₂',
    structure: {
      charge: 0,
      atoms: [atom('N', ORIGIN, 1, 'dot'), atom('N', from(0, 0, 0), 1, 'cross')],
      bonds: [{ from: 0, to: 1, order: 3 }],
    },
    note: 'Each nitrogen needs 3 more electrons, so the atoms share three pairs: a triple bond.',
  },
  {
    id: 'carbon-dioxide',
    name: 'Carbon dioxide',
    formula: 'CO₂',
    structure: {
      charge: 0,
      atoms: [atom('C', ORIGIN, 0, 'cross'), atom('O', from(0, 0, 180), 2, 'dot'), atom('O', from(0, 0, 0), 2, 'dot')],
      bonds: starBonds(2, 2),
    },
    note: 'Carbon needs 4 electrons and each oxygen needs 2, so carbon forms a double bond with each oxygen.',
  },
];

/* ------------------------------------------------------ structure types */

export type StructureType = 'ionic' | 'simple-molecular' | 'covalent-network' | 'metallic';

export const STRUCTURE_LABELS: Record<StructureType, string> = {
  ionic: 'Ionic lattice',
  'simple-molecular': 'Simple molecular',
  'covalent-network': 'Covalent network',
  metallic: 'Metallic',
};

export const STRUCTURE_ORDER: StructureType[] = ['ionic', 'simple-molecular', 'covalent-network', 'metallic'];

/** Aqueous behaviour of the pure substance placed in water near 25 °C. */
export type AqueousBehaviour = 'conducts' | 'does-not-conduct' | 'insoluble' | 'reacts';

export interface StructureSubstance {
  id: string;
  name: string;
  formula: string;
  structure: StructureType;
  /** Melting point at 100 kPa (°C). Null when the solid does not melt at 100 kPa. */
  meltingPointC: number | null;
  /** Shown instead of a number when the substance does not melt normally. */
  meltingLabel?: string;
  conductsSolid: boolean;
  /** Null when the substance does not melt at 100 kPa. */
  conductsMolten: boolean | null;
  aqueous: AqueousBehaviour;
  solubility: string;
  /** Particle-level reason for the properties. */
  explanation: string;
  /** A known exception or trap the learner should notice. */
  exception?: string;
}

export const STRUCTURE_SUBSTANCES: StructureSubstance[] = [
  {
    id: 'sodium-chloride',
    name: 'Sodium chloride',
    formula: 'NaCl',
    structure: 'ionic',
    meltingPointC: 801,
    conductsSolid: false,
    conductsMolten: true,
    aqueous: 'conducts',
    solubility: 'Soluble in water',
    explanation:
      'Na⁺ and Cl⁻ ions are held by strong electrostatic attraction in a lattice. The ions are fixed in the solid but free to move when molten or dissolved.',
  },
  {
    id: 'magnesium-oxide',
    name: 'Magnesium oxide',
    formula: 'MgO',
    structure: 'ionic',
    meltingPointC: 2852,
    conductsSolid: false,
    conductsMolten: true,
    aqueous: 'insoluble',
    solubility: 'Practically insoluble in water',
    explanation:
      'Mg²⁺ and O²⁻ carry double charges, so the lattice attraction is very strong and the melting point is very high.',
    exception: 'Not every ionic compound dissolves in water — magnesium oxide barely does.',
  },
  {
    id: 'potassium-chloride',
    name: 'Potassium chloride',
    formula: 'KCl',
    structure: 'ionic',
    meltingPointC: 771,
    conductsSolid: false,
    conductsMolten: true,
    aqueous: 'conducts',
    solubility: 'Soluble in water',
    explanation: 'K⁺ and Cl⁻ ions form a lattice. Ions only carry current once they are free to move.',
  },
  {
    id: 'water',
    name: 'Water',
    formula: 'H₂O',
    structure: 'simple-molecular',
    meltingPointC: 0,
    conductsSolid: false,
    conductsMolten: false,
    aqueous: 'does-not-conduct',
    solubility: 'It is the solvent',
    explanation:
      'Separate H₂O molecules are held together by intermolecular forces (including hydrogen bonds). Melting breaks only these, not the covalent bonds inside each molecule.',
    exception: 'Tap water conducts slightly because of dissolved ions; pure water is a very poor conductor.',
  },
  {
    id: 'methane',
    name: 'Methane',
    formula: 'CH₄',
    structure: 'simple-molecular',
    meltingPointC: -182,
    conductsSolid: false,
    conductsMolten: false,
    aqueous: 'insoluble',
    solubility: 'Very slightly soluble in water',
    explanation: 'Only weak dispersion forces hold CH₄ molecules together, so it melts far below 0 °C.',
  },
  {
    id: 'iodine',
    name: 'Iodine',
    formula: 'I₂',
    structure: 'simple-molecular',
    meltingPointC: 114,
    conductsSolid: false,
    conductsMolten: false,
    aqueous: 'insoluble',
    solubility: 'Slightly soluble in water; dissolves well in non-polar solvents',
    explanation:
      'I₂ molecules have many electrons, so dispersion forces are stronger than in most small molecules — but still far weaker than ionic or covalent bonds.',
  },
  {
    id: 'ethanol',
    name: 'Ethanol',
    formula: 'C₂H₅OH',
    structure: 'simple-molecular',
    meltingPointC: -114,
    conductsSolid: false,
    conductsMolten: false,
    aqueous: 'does-not-conduct',
    solubility: 'Mixes with water in all proportions',
    explanation: 'Ethanol molecules stay whole when they dissolve, so the solution has no free ions to carry current.',
    exception: 'Dissolving in water does not make a substance ionic — ethanol dissolves but does not conduct.',
  },
  {
    id: 'diamond',
    name: 'Diamond',
    formula: 'C',
    structure: 'covalent-network',
    meltingPointC: null,
    meltingLabel: 'Does not melt at 100 kPa; changes above about 3500 °C',
    conductsSolid: false,
    conductsMolten: null,
    aqueous: 'insoluble',
    solubility: 'Insoluble',
    explanation:
      'Every carbon atom is covalently bonded to four others in a 3D network. All electrons are held in bonds, so there are no mobile charges.',
  },
  {
    id: 'graphite',
    name: 'Graphite',
    formula: 'C',
    structure: 'covalent-network',
    meltingPointC: null,
    meltingLabel: 'Does not melt at 100 kPa; sublimes at about 3640 °C',
    conductsSolid: true,
    conductsMolten: null,
    aqueous: 'insoluble',
    solubility: 'Insoluble',
    explanation:
      'Each carbon bonds to three others in flat layers. The fourth outer electron of each atom is delocalised along the layer, so graphite conducts.',
    exception: 'Graphite conducts as a solid but is not a metal. Check the very high sublimation temperature.',
  },
  {
    id: 'silicon-dioxide',
    name: 'Silicon dioxide (quartz)',
    formula: 'SiO₂',
    structure: 'covalent-network',
    meltingPointC: 1713,
    conductsSolid: false,
    conductsMolten: false,
    aqueous: 'insoluble',
    solubility: 'Insoluble',
    explanation:
      'Each silicon bonds to four oxygens and each oxygen to two silicons in a giant network. Melting must break strong covalent bonds.',
  },
  {
    id: 'sodium',
    name: 'Sodium',
    formula: 'Na',
    structure: 'metallic',
    meltingPointC: 98,
    conductsSolid: true,
    conductsMolten: true,
    aqueous: 'reacts',
    solubility: 'Reacts with water (do not test)',
    explanation: 'Na⁺ ions sit in a sea of delocalised electrons that move freely in the solid and the liquid.',
    exception:
      'Sodium is metallic but melts at only 98 °C: each atom gives only one delocalised electron and the ions are large.',
  },
  {
    id: 'copper',
    name: 'Copper',
    formula: 'Cu',
    structure: 'metallic',
    meltingPointC: 1085,
    conductsSolid: true,
    conductsMolten: true,
    aqueous: 'insoluble',
    solubility: 'Insoluble',
    explanation: 'Delocalised electrons carry current and hold the positive ions together in a strong lattice.',
  },
  {
    id: 'iron',
    name: 'Iron',
    formula: 'Fe',
    structure: 'metallic',
    meltingPointC: 1538,
    conductsSolid: true,
    conductsMolten: true,
    aqueous: 'insoluble',
    solubility: 'Insoluble (rusts slowly in moist air)',
    explanation: 'Strong metallic bonding between Fe ions and delocalised electrons gives a high melting point.',
  },
  {
    id: 'mercury',
    name: 'Mercury',
    formula: 'Hg',
    structure: 'metallic',
    meltingPointC: -39,
    conductsSolid: true,
    conductsMolten: true,
    aqueous: 'insoluble',
    solubility: 'Insoluble',
    explanation: 'Mercury is a liquid metal at room temperature, but its delocalised electrons still carry current.',
    exception: 'A low melting point does not rule out metallic bonding — mercury melts at −39 °C.',
  },
];

/* ------------------------------------------------------------- VSEPR */

export type ShapeName =
  | 'linear'
  | 'trigonal planar'
  | 'bent'
  | 'tetrahedral'
  | 'trigonal pyramidal'
  | 'trigonal bipyramidal';

export const SHAPE_ORDER: ShapeName[] = [
  'linear',
  'trigonal planar',
  'bent',
  'tetrahedral',
  'trigonal pyramidal',
  'trigonal bipyramidal',
];

export interface VseprSpeciesCase {
  id: string;
  name: string;
  formula: string;
  structure: MolecularStructure;
  /** Index of the atom whose electron domains the learner counts. */
  centralIndex: number;
  /** Measured or standard bond angle label for display. */
  angleLabel: string;
  /** Representative angle in degrees for the shape sketch. */
  angleDegrees: number;
  /** Octet exception the learner must be told about. */
  octetNote?: string;
  /** True when the species needs an expanded octet (beyond some syllabuses). */
  expandedOctet?: boolean;
  note: string;
}

const H_UP_LEFT = 210;
const H_DOWN_LEFT = 150;
const H_UP_RIGHT = 330;
const H_DOWN_RIGHT = 30;
/** Five evenly spaced directions for a flat Lewis drawing of PCl₅ (connectivity only). */
const PENTAGON_ANGLES = [270, 342, 54, 126, 198];

export const VSEPR_SPECIES_CASES: VseprSpeciesCase[] = [
  {
    id: 'water',
    name: 'Water',
    formula: 'H₂O',
    centralIndex: 0,
    angleLabel: '104.5°',
    angleDegrees: 104.5,
    note: 'Two lone pairs repel more strongly than bonding pairs and squeeze the H–O–H angle below 109.5°.',
    structure: {
      charge: 0,
      atoms: [atom('O', ORIGIN, 2, 'cross'), atom('H', from(0, 0, H_DOWN_LEFT), 0, 'dot'), atom('H', from(0, 0, H_DOWN_RIGHT), 0, 'dot')],
      bonds: starBonds(2),
    },
  },
  {
    id: 'ammonia',
    name: 'Ammonia',
    formula: 'NH₃',
    centralIndex: 0,
    angleLabel: '107°',
    angleDegrees: 107,
    note: 'One lone pair pushes the three N–H bonds slightly closer than the tetrahedral 109.5°.',
    structure: {
      charge: 0,
      atoms: [
        atom('N', ORIGIN, 1, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
      ],
      bonds: starBonds(3),
    },
  },
  {
    id: 'methane',
    name: 'Methane',
    formula: 'CH₄',
    centralIndex: 0,
    angleLabel: '109.5°',
    angleDegrees: 109.5,
    note: 'Four bonding domains spread as far apart as possible in 3D: a tetrahedron.',
    structure: {
      charge: 0,
      atoms: [
        atom('C', ORIGIN, 0, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
        atom('H', from(0, 0, 270), 0, 'dot'),
      ],
      bonds: starBonds(4),
    },
  },
  {
    id: 'carbon-tetrachloride',
    name: 'Tetrachloromethane',
    formula: 'CCl₄',
    centralIndex: 0,
    angleLabel: '109.5°',
    angleDegrees: 109.5,
    note: 'Each C–Cl bond is polar, but four identical bonds in a tetrahedron cancel, so the molecule is non-polar.',
    structure: {
      charge: 0,
      atoms: [
        atom('C', ORIGIN, 0, 'cross'),
        atom('Cl', from(0, 0, 180), 3, 'dot'),
        atom('Cl', from(0, 0, 0), 3, 'dot'),
        atom('Cl', from(0, 0, 90), 3, 'dot'),
        atom('Cl', from(0, 0, 270), 3, 'dot'),
      ],
      bonds: starBonds(4),
    },
  },
  {
    id: 'chloroform',
    name: 'Trichloromethane',
    formula: 'CHCl₃',
    centralIndex: 0,
    angleLabel: 'about 109.5° (slightly distorted)',
    angleDegrees: 109.5,
    note: 'Replacing one Cl with H breaks the symmetry, so the bond dipoles no longer cancel and the molecule is polar.',
    structure: {
      charge: 0,
      atoms: [
        atom('C', ORIGIN, 0, 'cross'),
        atom('H', from(0, 0, 270), 0, 'dot'),
        atom('Cl', from(0, 0, 180), 3, 'dot'),
        atom('Cl', from(0, 0, 0), 3, 'dot'),
        atom('Cl', from(0, 0, 90), 3, 'dot'),
      ],
      bonds: starBonds(4),
    },
  },
  {
    id: 'carbon-dioxide',
    name: 'Carbon dioxide',
    formula: 'CO₂',
    centralIndex: 0,
    angleLabel: '180°',
    angleDegrees: 180,
    note: 'Each double bond counts as one electron domain. Two domains sit on opposite sides: linear.',
    structure: {
      charge: 0,
      atoms: [atom('C', ORIGIN, 0, 'cross'), atom('O', from(0, 0, 180), 2, 'dot'), atom('O', from(0, 0, 0), 2, 'dot')],
      bonds: starBonds(2, 2),
    },
  },
  {
    id: 'boron-trifluoride',
    name: 'Boron trifluoride',
    formula: 'BF₃',
    centralIndex: 0,
    angleLabel: '120°',
    angleDegrees: 120,
    octetNote: 'Boron has only 6 outer electrons in BF₃ — an incomplete octet.',
    note: 'Three bonding domains and no lone pairs spread out flat at 120°.',
    structure: {
      charge: 0,
      atoms: [
        atom('B', ORIGIN, 0, 'cross'),
        atom('F', from(0, 0, 270), 3, 'dot'),
        atom('F', from(0, 0, H_DOWN_LEFT), 3, 'dot'),
        atom('F', from(0, 0, H_DOWN_RIGHT), 3, 'dot'),
      ],
      bonds: starBonds(3),
    },
  },
  {
    id: 'sulfur-dioxide',
    name: 'Sulfur dioxide',
    formula: 'SO₂',
    centralIndex: 0,
    angleLabel: 'about 119°',
    angleDegrees: 119,
    octetNote:
      'Shown as one octet structure (O=S–O). Two equivalent structures exist (resonance); the real S–O bonds are identical.',
    note: 'Two bonding domains and one lone pair on sulfur give three domains: a bent shape just under 120°.',
    structure: {
      charge: 0,
      atoms: [
        atom('S', ORIGIN, 1, 'cross'),
        atom('O', from(0, 0, H_DOWN_LEFT), 2, 'dot'),
        atom('O', from(0, 0, H_DOWN_RIGHT), 3, 'dot'),
      ],
      bonds: [
        { from: 0, to: 1, order: 2 },
        { from: 0, to: 2, order: 1 },
      ],
    },
  },
  {
    id: 'ammonium',
    name: 'Ammonium ion',
    formula: 'NH₄⁺',
    centralIndex: 0,
    angleLabel: '109.5°',
    angleDegrees: 109.5,
    note: 'NH₃ uses its lone pair to bond to H⁺. Four bonding domains: tetrahedral.',
    structure: {
      charge: 1,
      atoms: [
        atom('N', ORIGIN, 0, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
        atom('H', from(0, 0, 270), 0, 'dot'),
      ],
      bonds: starBonds(4),
    },
  },
  {
    id: 'hydronium',
    name: 'Hydronium ion',
    formula: 'H₃O⁺',
    centralIndex: 0,
    angleLabel: 'less than 109.5° (about 107° by VSEPR)',
    angleDegrees: 107,
    note: 'Three bonding domains and one lone pair, like NH₃. Measured angles vary with the surroundings of the ion.',
    structure: {
      charge: 1,
      atoms: [
        atom('O', ORIGIN, 1, 'cross'),
        atom('H', from(0, 0, 180), 0, 'dot'),
        atom('H', from(0, 0, 0), 0, 'dot'),
        atom('H', from(0, 0, 90), 0, 'dot'),
      ],
      bonds: starBonds(3),
    },
  },
  {
    id: 'hydrogen-cyanide',
    name: 'Hydrogen cyanide',
    formula: 'HCN',
    centralIndex: 1,
    angleLabel: '180°',
    angleDegrees: 180,
    note: 'Carbon has a single bond to H and a triple bond to N: two domains, linear. The ends differ, so it is polar.',
    structure: {
      charge: 0,
      atoms: [atom('H', from(0, 0, 180), 0, 'dot'), atom('C', ORIGIN, 0, 'cross'), atom('N', from(0, 0, 0), 1, 'dot')],
      bonds: [
        { from: 0, to: 1, order: 1 },
        { from: 1, to: 2, order: 3 },
      ],
    },
  },
  {
    id: 'ethene',
    name: 'Ethene',
    formula: 'C₂H₄',
    centralIndex: 0,
    angleLabel: 'about 120° (H–C–H 117°)',
    angleDegrees: 120,
    note: 'Each carbon has three domains (two C–H and one C=C), so each carbon is trigonal planar and the molecule is flat.',
    structure: {
      charge: 0,
      atoms: [
        atom('C', { x: -0.5, y: 0 }, 0, 'cross'),
        atom('C', { x: 0.5, y: 0 }, 0, 'cross'),
        atom('H', from(-0.5, 0, H_UP_LEFT), 0, 'dot'),
        atom('H', from(-0.5, 0, H_DOWN_LEFT), 0, 'dot'),
        atom('H', from(0.5, 0, H_UP_RIGHT), 0, 'dot'),
        atom('H', from(0.5, 0, H_DOWN_RIGHT), 0, 'dot'),
      ],
      bonds: [
        { from: 0, to: 1, order: 2 },
        { from: 0, to: 2, order: 1 },
        { from: 0, to: 3, order: 1 },
        { from: 1, to: 4, order: 1 },
        { from: 1, to: 5, order: 1 },
      ],
    },
  },
  {
    id: 'ethyne',
    name: 'Ethyne',
    formula: 'C₂H₂',
    centralIndex: 0,
    angleLabel: '180°',
    angleDegrees: 180,
    note: 'Each carbon has two domains (one C–H and one C≡C), so the whole molecule is linear.',
    structure: {
      charge: 0,
      atoms: [
        atom('C', { x: -0.5, y: 0 }, 0, 'cross'),
        atom('C', { x: 0.5, y: 0 }, 0, 'cross'),
        atom('H', from(-0.5, 0, 180), 0, 'dot'),
        atom('H', from(0.5, 0, 0), 0, 'dot'),
      ],
      bonds: [
        { from: 0, to: 1, order: 3 },
        { from: 0, to: 2, order: 1 },
        { from: 1, to: 3, order: 1 },
      ],
    },
  },
  {
    id: 'phosphorus-pentachloride',
    name: 'Phosphorus pentachloride',
    formula: 'PCl₅',
    centralIndex: 0,
    angleLabel: '90° and 120°',
    angleDegrees: 120,
    expandedOctet: true,
    octetNote:
      'Expanded octet: phosphorus has 10 outer electrons here. This case is beyond some Australian senior syllabuses.',
    note: 'Five bonding domains: three in a flat triangle (120°) and two above and below (90°).',
    structure: {
      charge: 0,
      atoms: [
        atom('P', ORIGIN, 0, 'cross'),
        ...PENTAGON_ANGLES.map((angle) => atom('Cl', from(0, 0, angle), 3, 'dot')),
      ],
      bonds: starBonds(5),
    },
  },
];
